import { useCallback, useEffect, useState } from 'react';
import { useCartContext } from '../../../components/providers/CartProvider';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Minus, Plus, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { BrandButton } from '../../../components/ui/index.js';
import { getThemeContext } from '../../../components/providers/ThemeProvider.jsx';
import { confirmDelete } from '../../../components/HelperAlerts/HelpersAlerts.jsx';
import CommerceItemImage from '../../../components/store/CommerceItemImage.jsx';

const fmt = (n, currency = 'EUR') =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: currency || 'EUR' }).format(n);

// ── Quantity stepper ───────────────────────────────────────────────────────────
const stepperBtn =
    'w-8 h-8 rounded-lg border border-white/15 dark:bg-base-200 bg-white ' +
    'flex items-center justify-center hover:border-brand-red/50 transition-colors ' +
    'disabled:opacity-40 disabled:cursor-not-allowed';

const getItemDetail = (item) => {
    if (item?.item_type === 'custom_order') {
        const modelId = item?.custom_order_request_detail?.customized_keyring?.id;
        return modelId ? { path: `/viewer/${modelId}`, label: 'Ver modelo 3D' } : null;
    }
    if (item?.item_type === 'digital') {
        const designId = item?.digital_package_detail?.design_id;
        return designId ? { path: `/viewer/${designId}`, label: 'Ver diseño' } : null;
    }

    const productId = item?.product?.id;
    return productId ? { path: `/product/${productId}`, label: 'Ver producto' } : null;
};

// Líneas con cantidad fija (no editable desde el carrito): propuesta
// personalizada aprobada y paquete digital (siempre 1 licencia/unidad).
const isFixedQuantity = (item) => item?.item_type === 'custom_order' || item?.item_type === 'digital';

// ── Main component ─────────────────────────────────────────────────────────────
const CartPage = () => {
    const { cartItems, setCartItems, updateCartItemQuantity } = useCartContext();
    const [loading, setLoading] = useState(false);
    const [error, setError]   = useState(null);
    const [notes, setNotes]   = useState('');
    const navigate = useNavigate();
    const theme = getThemeContext();

    const fetchCartItems = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await axios.get('/cart/', { withCredentials: true });
            setCartItems(r.data.items || []);
        } catch (err) {
            console.error('Error al obtener carrito:', err);
            setError('No se pudieron cargar los productos del carrito.');
        } finally {
            setLoading(false);
        }
    }, [setCartItems]);

    const handleRemove = async (itemId, productName) => {
        const confirmed = await confirmDelete({
            title: '¿Eliminar producto?',
            text: productName ? `"${productName}" se eliminará del carrito` : 'Este artículo se eliminará del carrito',
            theme,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        });
        if (!confirmed) return;

        setLoading(true);
        try {
            await axios.delete(`/cart/items/${itemId}/`, { withCredentials: true });
            setCartItems(prev => prev.filter(i => i.id !== itemId));
        } catch (err) {
            console.error('Error al eliminar:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleQty = (itemId, qty) => {
        if (qty < 1) return;
        updateCartItemQuantity(itemId, qty);
    };

    const subtotal = (cartItems || []).reduce((acc, item) => {
        if (isFixedQuantity(item)) {
            return acc + (parseFloat(item?.product_price) || 0);
        }
        return acc + (parseFloat(item?.product_price) || 0) * (item?.quantity || 0);
    }, 0);

    useEffect(() => { fetchCartItems(); }, [fetchCartItems]);

    // ── Empty state ───────────────────────────────────────────────────────────
    const isEmpty = !loading && !error && (cartItems?.length ?? 0) === 0;

    return (
        <div className="h-full w-full overflow-y-scroll" style={{ minHeight: 0 }}>
            <div className="px-4 md:px-10 lg:px-20 py-6 flex flex-col gap-6">

                {/* Header */}
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl md:text-3xl font-bold dark:text-white text-gray-900">
                        Tu carrito
                    </h1>
                    {!isEmpty && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brand-red/10 text-brand-red">
                            {cartItems.length} {cartItems.length === 1 ? 'artículo' : 'artículos'}
                        </span>
                    )}
                </div>

                {/* Loading */}
                {loading && (
                    <div className="flex items-center justify-center py-20">
                        <span className="loading loading-spinner loading-lg text-brand-red" />
                    </div>
                )}

                {/* Error */}
                {error && !loading && (
                    <p className="text-red-400 text-sm">{error}</p>
                )}

                {/* Empty */}
                {isEmpty && (
                    <motion.div
                        className="flex flex-col items-center justify-center gap-5 py-24"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <ShoppingCart size={56} className="text-gray-300 dark:text-gray-600" />
                        <div className="text-center">
                            <h2 className="text-xl font-semibold dark:text-white text-gray-800 mb-1">
                                Tu carrito está vacío
                            </h2>
                            <p className="text-gray-400 text-sm">
                                ¿Listo para encontrar algo que te guste?
                            </p>
                        </div>
                        <BrandButton as="link" to="/store" size="md">
                            Ir a la tienda
                        </BrandButton>
                    </motion.div>
                )}

                {/* Content */}
                {!loading && !error && !isEmpty && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

                        {/* ── Product list ─────────────────────────────── */}
                        <div className="lg:col-span-2 glassCard p-4 md:p-6 rounded-2xl flex flex-col gap-2">
                            {/* Column headers */}
                            <div className="hidden sm:grid grid-cols-[1fr_auto_auto] gap-4 pb-3 border-b border-white/10 text-xs uppercase tracking-widest text-gray-400 font-medium">
                                <span>Producto</span>
                                <span className="text-center">Cantidad</span>
                                <span className="text-right">Total</span>
                            </div>

                            {/* Items */}
                            {cartItems.map((item, idx) => {
                                const detail = getItemDetail(item);
                                return (
                                    <motion.div
                                        key={`${item.id}_${idx}`}
                                        className="flex flex-col sm:flex-row items-start sm:items-center gap-4 py-4 border-b border-white/10 last:border-0"
                                        initial={{ opacity: 0, x: -12 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.3, delay: idx * 0.06 }}
                                    >
                                    {/* Image + info */}
                                    <div className="flex items-center gap-4 flex-1 min-w-0">
                                        {detail ? (
                                            <button
                                                type="button"
                                                onClick={() => navigate(detail.path)}
                                                className="rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                                aria-label={detail.label}
                                            >
                                                <CommerceItemImage
                                                    item={item}
                                                    customKeyring={item.item_type === 'digital' ? item.digital_package_detail : undefined}
                                                    alt={item.product_name}
                                                    className="w-20 h-20"
                                                    show3dOnHover
                                                />
                                            </button>
                                        ) : (
                                            <CommerceItemImage
                                                item={item}
                                                customKeyring={item.item_type === 'digital' ? item.digital_package_detail : undefined}
                                                alt={item.product_name}
                                                className="w-20 h-20"
                                                show3dOnHover
                                            />
                                        )}
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {detail ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate(detail.path)}
                                                        className="font-semibold dark:text-white text-gray-900 truncate hover:text-primary transition-colors text-left"
                                                    >
                                                        {item.product_name}
                                                    </button>
                                                ) : (
                                                    <p className="font-semibold dark:text-white text-gray-900 truncate">
                                                        {item.product_name}
                                                    </p>
                                                )}
                                                {item.item_type === 'custom_order' && (
                                                    <span className="badge badge-sm badge-success">Diseño aprobado</span>
                                                )}
                                                {item.item_type === 'digital' && (
                                                    <span className="badge badge-sm badge-info">Paquete digital</span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <p className="text-sm text-gray-400">
                                                    {isFixedQuantity(item)
                                                        ? fmt(item.product_price, item.snapshot_currency)
                                                        : `${fmt(item.product_price)} / ud.`}
                                                </p>
                                                {isFixedQuantity(item) && (
                                                    <span className="badge badge-xs bg-blue-500/20 text-blue-300 border-blue-500/30 tooltip"
                                                          data-tip="Este precio está congelado y no cambiará aunque se actualice después">
                                                        🔒 Bloqueado
                                                    </span>
                                                )}
                                            </div>
                                            {item.item_type === 'custom_order' && (
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {item.snapshot_material} · {item.snapshot_color}
                                                    {item.snapshot_finish ? ` · ${item.snapshot_finish}` : ''}
                                                </p>
                                            )}
                                            {item.item_type === 'digital' && (
                                                <p className="text-xs text-gray-400 mt-1">
                                                    Requiere licencia {item.snapshot_required_license_version || '—'} ·
                                                    {' '}se habilita tras confirmar el pago
                                                </p>
                                            )}
                                            {detail && (
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(detail.path)}
                                                    className="btn btn-ghost btn-xs mt-2 px-0 gap-1 text-primary hover:bg-transparent"
                                                >
                                                    <Eye size={14} />
                                                    {detail.label}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Stepper + delete */}
                                    <div className="flex items-center gap-3 sm:gap-4">
                                        {isFixedQuantity(item) ? (
                                            <div className="text-sm font-semibold dark:text-white text-gray-900">
                                                {item.item_type === 'digital' ? '1 licencia' : `${item.quantity} uds.`}
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1">
                                                <button
                                                    className={stepperBtn}
                                                    onClick={() => handleQty(item.id, item.quantity - 1)}
                                                    disabled={loading || item.quantity <= 1}
                                                >
                                                    <Minus size={12} />
                                                </button>
                                                <span className="w-10 text-center font-semibold dark:text-white text-gray-900 text-sm">
                                                    {item.quantity}
                                                </span>
                                                <button
                                                    className={stepperBtn}
                                                    onClick={() => handleQty(item.id, item.quantity + 1)}
                                                    disabled={loading}
                                                >
                                                    <Plus size={12} />
                                                </button>
                                            </div>
                                        )}

                                        <button
                                            className="text-gray-400 hover:text-red-400 transition-colors p-1"
                                            onClick={() => handleRemove(item.id, item.product_name)}
                                            disabled={loading}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>

                                    {/* Line total */}
                                    <p className="font-bold dark:text-white text-gray-900 sm:min-w-[80px] sm:text-right">
                                        {fmt(
                                            isFixedQuantity(item)
                                                ? (parseFloat(item.product_price) || 0)
                                                : (parseFloat(item.product_price) || 0) * item.quantity,
                                            item.snapshot_currency || 'EUR'
                                        )}
                                    </p>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* ── Summary ──────────────────────────────────── */}
                        <div className="glassCard p-5 rounded-2xl flex flex-col gap-4 sticky top-4">
                            <h2 className="font-bold text-lg dark:text-white text-gray-900">
                                Resumen del pedido
                            </h2>

                            {/* Notes */}
                            <textarea
                                rows={3}
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                placeholder="Instrucciones especiales para el pedido..."
                                className="w-full bg-transparent border border-white/15 rounded-xl p-3 text-sm resize-none focus:outline-none focus:border-brand-red/50 transition-colors text-gray-700 dark:text-gray-300 placeholder-gray-400"
                            />

                            {/* Lines */}
                            <div className="flex flex-col gap-2 text-sm">
                                <div className="flex justify-between text-gray-500 dark:text-gray-400">
                                    <span>Subtotal</span>
                                    <span>{fmt(subtotal)}</span>
                                </div>
                                <div className="flex justify-between text-gray-500 dark:text-gray-400">
                                    <span>Envío</span>
                                    <span className="text-xs italic">Calculado al finalizar</span>
                                </div>
                            </div>

                            <div className="border-t border-white/10 pt-3 flex justify-between">
                                <span className="font-bold text-lg dark:text-white text-gray-900">Total</span>
                                <span className="font-bold text-xl dark:text-white text-gray-900">
                                    {fmt(subtotal)}
                                </span>
                            </div>

                            <BrandButton
                                variant="solid" size="md" className="w-full justify-center"
                                disabled={loading || !cartItems?.length}
                                onClick={() => navigate('/checkout')}
                            >
                                Proceder al pago
                            </BrandButton>

                            <BrandButton
                                variant="ghost"
                                size="sm"
                                className="w-full justify-center"
                                onClick={() => navigate('/store')}
                            >
                                ← Continuar comprando
                            </BrandButton>

                            <p className="text-center text-xs text-gray-400">
                                Impuestos y envío calculados al finalizar la compra
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CartPage;
